import React, { useEffect, useState } from 'react';
import styles from './WidgetController.module.css';

/** Only known third-party chat roots — avoid [class*="widget"] / [class*="chat"] (matches page UI). */
const CHAT_LAUNCHER_SELECTORS = [
  '#replain-widget',
  '[id^="replain-widget"]',
  '[id*="replain"][id*="widget"]',
  '.replain-widget',
  '[data-replain]',
  'iframe[src*="replain"]',
  'iframe[src*="replain."]',
];

function collectChatRoots() {
  const seen = new Set();
  const out = [];
  CHAT_LAUNCHER_SELECTORS.forEach((selector) => {
    try {
      document.querySelectorAll(selector).forEach((el) => {
        if (el && !seen.has(el)) {
          seen.add(el);
          out.push(el);
        }
      });
    } catch {
      /* invalid selector in old browsers */
    }
  });
  return out;
}

function applyChatPosition(el) {
  if (!el || !el.style) return;
  try {
    el.style.setProperty('position', 'fixed', 'important');
    el.style.setProperty('left', 'auto', 'important');
    el.style.setProperty('top', 'auto', 'important');
    el.style.setProperty('right', '20px', 'important');
    el.style.setProperty('bottom', '20px', 'important');
    el.style.setProperty('margin', '0', 'important');
    el.style.setProperty('z-index', '99999', 'important');
    /* Replain often centers with translate(-50%,-50%); reset so bottom/right stick */
    el.style.setProperty('transform', 'translate3d(0, 0, 0) scale(0.95)', 'important');
    el.style.setProperty('max-width', 'min(100vw - 24px, 360px)', 'important');
    el.style.setProperty('max-height', 'min(100vh - 24px, 560px)', 'important');
    el.classList.add('custom-gtm-widget-position');
  } catch (e) {
    console.warn('Widget position:', e);
  }
}

const WidgetController = () => {
  const [widgetVisible, setWidgetVisible] = useState(true);
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 45;

    const run = () => {
      if (cancelled) return;
      collectChatRoots().forEach(applyChatPosition);
    };

    const intervalId = setInterval(() => {
      attempts += 1;
      run();
      if (attempts >= maxAttempts) clearInterval(intervalId);
    }, 1000);

    const mo = new MutationObserver(() => {
      run();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    run();
    const t0 = setTimeout(run, 300);
    const t1 = setTimeout(run, 1500);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      clearTimeout(t0);
      clearTimeout(t1);
      mo.disconnect();
    };
  }, []);

  const toggleWidget = () => {
    const widgets = collectChatRoots();

    if (widgets.length > 0) {
      if (widgetVisible) {
        widgets.forEach((widget) => {
          try {
            widget.style.setProperty('display', 'none', 'important');
          } catch (e) {
            console.log('Error hiding widget:', e);
          }
        });
        setWidgetVisible(false);
      } else {
        widgets.forEach((widget) => {
          try {
            widget.style.setProperty('display', '', 'important');
            applyChatPosition(widget);
          } catch (e) {
            console.log('Error showing widget:', e);
          }
        });
        setWidgetVisible(true);
      }
    } else {
      console.log('No widgets found for toggle');
    }
  };

  return (
    <div className={styles.widgetController}>
      <button
        onClick={toggleWidget}
        className={styles.toggleButton}
        title={widgetVisible ? 'Скрыть чат' : 'Показать чат'}
      >
        {widgetVisible ? 'Скрыть чат' : 'Чат'}
      </button>

      {process.env.NODE_ENV === 'development' && (
        <button
          onClick={() => setDebugMode(!debugMode)}
          className={styles.debugButton}
          title="Debug mode"
        >
          🐛
        </button>
      )}

      {debugMode && (
        <div className={styles.debugInfo}>
          <p>Widget Visible: {widgetVisible ? 'Yes' : 'No'}</p>
          <p>Browser: {navigator.userAgent}</p>
          <p>Time: {new Date().toLocaleTimeString()}</p>
        </div>
      )}
    </div>
  );
};

export default WidgetController;
